// components/tier-branch/TierCard.tsx
// A single tier row in the Tier Overview: name, qualifying range, max
// transactions, Manual / Active toggles, and edit / users / delete actions.
import React from "react";
import {
  Card, Box, Typography, Chip, IconButton, Tooltip, Switch, FormControlLabel,
  Button, Divider,
} from "@mui/material";
import { Edit, Delete, People, Add } from "@mui/icons-material";
import type { Tier, Branch } from "../../types/tierBranch";
import { fmt } from "./useSnack";

const TierCard: React.FC<{
  tier: Tier;
  canManage: boolean;
  onEdit: (t: Tier) => void;
  onDelete: (t: Tier) => void;
  onViewUsers: (t: Tier) => void;
  onToggle: (
    t: Tier,
    patch: { isActive?: boolean; isManual?: boolean; manualPaymentEnabled?: boolean }
  ) => void;
  onAddBranch: (t: Tier) => void;
  onToggleBranch: (b: Branch) => void;
  onDeleteBranch: (b: Branch) => void;
}> = ({
  tier, canManage, onEdit, onDelete, onViewUsers, onToggle,
  onAddBranch, onToggleBranch, onDeleteBranch,
}) => {
  const branches = tier.branches || [];
  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{tier.name}</Typography>
            {typeof tier.userCount === "number" && (
              <Chip size="small" label={`${tier.userCount} users`} />
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              • Amount: ₹{fmt(tier.minTransactionAmount)} – ₹{fmt(tier.maxTransactionAmount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Max Transactions: {tier.maxTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Deposit: {tier.allowDeposit ? `${fmt(tier.minDeposit)}–${fmt(tier.maxDeposit)}` : "off"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Withdraw: {tier.allowWithdraw ? `${fmt(tier.minWithdraw)}–${fmt(tier.maxWithdraw)}` : "off"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={tier.isManual}
                disabled={!canManage}
                onChange={(e) => onToggle(tier, { isManual: e.target.checked })}
              />
            }
            label={
              <Tooltip title="Hand-managed tier — auto-assign skips it">
                <span>Manual assign</span>
              </Tooltip>
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={tier.manualPaymentEnabled}
                disabled={!canManage}
                onChange={(e) =>
                  onToggle(tier, { manualPaymentEnabled: e.target.checked })
                }
              />
            }
            label={
              <Tooltip title="Players on this tier can deposit / withdraw by bank or UPI">
                <span>Manual pay</span>
              </Tooltip>
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={tier.isActive}
                disabled={!canManage}
                onChange={(e) => onToggle(tier, { isActive: e.target.checked })}
              />
            }
            label="Active"
          />
          <Tooltip title="View users">
            <IconButton size="small" onClick={() => onViewUsers(tier)}>
              <People fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
            <>
              <Tooltip title="Edit tier">
                <IconButton size="small" onClick={() => onEdit(tier)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete tier">
                <IconButton size="small" color="error" onClick={() => onDelete(tier)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Branches that belong to this tier (like the video's tier rows) */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">Branches</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip size="small" label={`${branches.length} branch${branches.length === 1 ? "" : "es"}`} />
          {canManage && (
            <Button size="small" color="success" startIcon={<Add />} onClick={() => onAddBranch(tier)}>
              Add Branch
            </Button>
          )}
        </Box>
      </Box>

      {branches.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No branches yet.
        </Typography>
      ) : (
        branches.map((b) => (
          <Box
            key={b.id}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}
          >
            <Typography variant="body2">{b.name}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={b.enabled}
                    disabled={!canManage}
                    onChange={() => onToggleBranch(b)}
                  />
                }
                label={b.enabled ? "Active" : "Off"}
              />
              {canManage && (
                <Tooltip title="Delete branch">
                  <IconButton size="small" color="error" onClick={() => onDeleteBranch(b)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))
      )}
    </Card>
  );
};

export default TierCard;
